import React, { Fragment, useState, useEffect } from "react";
import { CenteredModal, FormInput, FormInputIcon } from "~/components";
import { useForm, Controller } from "react-hook-form";
import useCreateUserAccountModal from "./useCreateUserAccountModal";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faTimes, faEnvelopeOpenText, faTriangleExclamation, faCopy } from "@fortawesome/free-solid-svg-icons";
import Select from "react-select";

export default function CreateUserAccountModal({ show, onClose }) {
    const [activeTab, setActiveTab] = useState("Personal");
    const [copied, setCopied] = useState(false);
    const [copiedUsername, setCopiedUsername] = useState(false);
    const [copiedPassword, setCopiedPassword] = useState(false);
    const [showSuperAdminPassword, setShowSuperAdminPassword] = useState(false);

    const {
        register,
        handleSubmit,
        reset,
        control,
        watch,
        setValue,
        getValues,
        formState: { errors }
    } = useForm();

    const codeValues = watch(["code0", "code1", "code2", "code3", "code4", "code5"]);
    const isComplete = codeValues.every(val => val !== undefined && val !== "");

    const {
        step,
        isSubmitting,
        error,
        successData,
        servicePlans,
        databases,
        selectedDb,
        setSelectedDb,
        inputsRef,
        handleInput,
        handleBeforeInput,
        handleKeyDown,
        sendCreationCode,
        createUserWithCode,
        resetModal,
        resendCountdown
    } = useCreateUserAccountModal(setValue, getValues, codeValues);

    useEffect(() => {
        if (show) {
            resetModal(reset);
            setActiveTab("Personal");
            setCopied(false);
        }
    }, [show]);

    const handleCopy = () => {
        if (successData) {
            navigator.clipboard.writeText(`Username: ${successData.UserName}\nPassword: ${successData.Password}`);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        }
    };

    const handleCopySingle = (text, type) => {
        if (text) {
            navigator.clipboard.writeText(text);
            if (type === 'username') {
                setCopiedUsername(true);
                setTimeout(() => setCopiedUsername(false), 2000);
            } else if (type === 'password') {
                setCopiedPassword(true);
                setTimeout(() => setCopiedPassword(false), 2000);
            }
        }
    };

    const onSubmit = (data) => {
        if (step === 1) {
            sendCreationCode(data);
        } else if (step === 2) {
            createUserWithCode(data);
        }
    };

    return (
        <CenteredModal
            show={show}
            onClose={onClose}
            title="Create User Account"
            formId={step === 3 ? undefined : "formCreateUserAccount"}
            confirmText={step === 1 ? "Send Verification Code" : step === 2 ? "Verify & Create User" : "Done"}
            modalType="modal-xl"
            isSubmitting={isSubmitting}
            hideFooter={step === 3}
            onConfirm={step === 3 ? onClose : undefined}
        >
            <div className="px-3 pb-0">
                {step === 1 && (
                    <Fragment>
                        <p className="text-muted fw-bold fs-9 mb-4 mt-2">
                            Create a new user account and directly assign a service plan. A verification code will be sent to the user's email.
                        </p>
                        <form id="formCreateUserAccount" onSubmit={handleSubmit(onSubmit)} autoComplete="off">
                            {error && (
                                <div className="auth-error-message mb-4 mt-0 d-flex justify-content-center gap-2">
                                    <FontAwesomeIcon icon={faTriangleExclamation} className="text-danger mt-1" />
                                    <span className="text-danger">{error}</span>
                                </div>
                            )}
                            <div style={{ border: '1px solid #d5dbdb', borderRadius: '8px', overflow: 'hidden', marginBottom: '20px' }}>
                                <div style={{ backgroundColor: '#f8f9fa', borderBottom: '1px solid #d5dbdb', padding: '12px 16px' }}>
                                    <h6 className="mb-0 fw-bold" style={{ color: '#16191f', fontSize: '0.9rem' }}>Account Details</h6>
                                    <p className="mb-0" style={{ fontSize: '0.8rem', color: '#687078' }}>Configure the basic information for the new user.</p>
                                </div>
                                <div style={{ padding: '20px 16px' }}>
                                    <div className="row g-4">
                                        <div className="col-12 col-md-7 pe-md-4">
                                            <div className="mb-4">
                                                <label className="d-block fw-bold mb-0" style={{ fontSize: '0.875rem', color: '#16191f' }}>
                                                    Target Email <span className="text-danger">*</span>
                                                </label>
                                                <p style={{ fontSize: '0.78rem', color: '#687078', margin: '2px 0 6px 0' }}>
                                                    The contact email address for this user.
                                                </p>
                                                <FormInput 
                                                    showHeaderLabel={false}
                                                    name="Email"
                                                    type="email"
                                                    errors={errors}
                                                    required={true}
                                                    maxLengthText=""
                                                    wrapperClassName="mb-0"
                                                    placeholder="Enter email address"
                                                    validation={{...register("Email", { required: "Email is required.", pattern: { value: /^\S+@\S+$/i, message: "Invalid email format" } })}}
                                                />
                                            </div>

                                            <div className="mb-3 mt-2" style={{ borderTop: '1px solid #eaeded', paddingTop: '16px' }}>
                                                <label className="d-block fw-bold mb-0" style={{ fontSize: '0.875rem', color: '#16191f' }}>
                                                    Service Plan
                                                </label>
                                                <p style={{ fontSize: '0.78rem', color: '#687078', margin: '2px 0 12px 0' }}>
                                                    Select the plan tier you want to assign to the user (optional).
                                                </p>
                                                <div className="d-flex align-items-center mb-3">
                                                    <button
                                                        type="button"
                                                        className={`btn rounded-pill px-4 me-2 ${activeTab === 'Personal' ? 'btn-primary' : 'btn-light text-secondary fw-semibold'}`}
                                                        onClick={() => setActiveTab('Personal')}
                                                    >
                                                        Personal
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className={`btn rounded-pill px-4 ${activeTab === 'Business' ? 'btn-primary' : 'btn-light text-secondary fw-semibold'}`}
                                                        onClick={() => setActiveTab('Business')}
                                                    >
                                                        Business
                                                    </button>
                                                </div>

                                                <div className="d-flex flex-column gap-2">
                                                    {servicePlans.filter(p => p.ServiceType === activeTab && p.Price > 0).map(plan => {
                                                        const isSelected = watch('ServicePlanId') == plan.ServicePlanId;
                                                        return (
                                                            <div key={plan.ServicePlanId}>
                                                                <label className="w-100 cursor-pointer mb-0">
                                                                    <input
                                                                        type="radio"
                                                                        value={plan.ServicePlanId}
                                                                        className="d-none"
                                                                        {...register('ServicePlanId')}
                                                                    />
                                                                    <div
                                                                        className={`card rounded-3 transition-all border`}
                                                                        style={{
                                                                            borderColor: isSelected ? '#0d6efd' : '#eaeded',
                                                                            backgroundColor: isSelected ? '#f8faff' : '#ffffff'
                                                                        }}
                                                                    >
                                                                        <div className="card-body p-2 px-3 d-flex align-items-center">
                                                                            <div className="me-3">
                                                                                <div
                                                                                    className="rounded-circle d-flex align-items-center justify-content-center"
                                                                                    style={{
                                                                                        width: '18px',
                                                                                        height: '18px',
                                                                                        border: `2px solid ${isSelected ? '#0d6efd' : '#ced4da'}`,
                                                                                        backgroundColor: '#ffffff'
                                                                                    }}
                                                                                >
                                                                                    {isSelected && <div className="rounded-circle bg-primary" style={{ width: '8px', height: '8px' }}></div>}
                                                                                </div>
                                                                            </div>
                                                                            <div>
                                                                                <div className="d-flex align-items-baseline gap-2 mb-0">
                                                                                    <span className="fw-bolder text-body-emphasis fs-8-1">{plan.Name}</span>
                                                                                    {plan.Price > 0 ? (
                                                                                        <Fragment>
                                                                                            <span className="fw-bold text-body-emphasis fs-8-1">${plan.Price}</span>
                                                                                            <span className="text-body-tertiary" style={{ fontSize: '0.75rem' }}>per {plan?.DurationUnit?.toLowerCase() || 'unit'}</span>
                                                                                        </Fragment>
                                                                                    ) : (
                                                                                        <span className="fw-bold text-success fs-8-1">FREE</span>
                                                                                    )}
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </label>
                                                            </div>
                                                        )
                                                    })}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-12 col-md-5">
                                            <div className="bg-light p-4 rounded-3 h-100 border d-flex flex-column" style={{ borderColor: '#eaeded', backgroundColor: '#f8f9fa' }}>
                                                <div className="flex-grow-1">
                                                    {(() => {
                                                        const selectedId = watch('ServicePlanId');
                                                        const selectedPlan = servicePlans.find(p => p.ServicePlanId == selectedId);
                                                        if (!selectedPlan) {
                                                            return <div className="text-body-tertiary fs-9 fw-semibold text-center mt-4">Please select a plan on the left to view its features.</div>;
                                                        }
                                                        return (
                                                            <Fragment>
                                                                <h6 className="fw-bold mb-4" style={{ color: '#16191f' }}>{selectedPlan.Name} plan includes:</h6>
                                                                <ul className="list-unstyled mb-0 d-flex flex-column gap-3">
                                                                    {selectedPlan.PlanDescriptionMappings?.map((mapping, idx) => (
                                                                        <li key={idx} className="d-flex align-items-center">
                                                                            <FontAwesomeIcon
                                                                                icon={mapping.IsApply ? faCheck : faTimes}
                                                                                className={`me-3 ${mapping.IsApply ? 'text-primary' : 'text-body-tertiary opacity-50'}`}
                                                                                style={{ width: '16px' }}
                                                                            />
                                                                            <span className={`fs-9 ${mapping.IsApply ? 'text-body-emphasis fw-medium' : 'text-body-tertiary text-decoration-line-through'}`}>
                                                                                {mapping.Description}
                                                                            </span>
                                                                        </li>
                                                                    ))}
                                                                </ul>
                                                            </Fragment>
                                                        );
                                                    })()}
                                                </div>
                                                <div className="mt-4 pt-4 border-top" style={{ borderColor: '#eaeded' }}>
                                                    <label className="d-block fw-bold mb-0" style={{ fontSize: '0.875rem', color: '#16191f' }}>
                                                        Target Database
                                                    </label>
                                                    <p style={{ fontSize: '0.78rem', color: '#687078', margin: '2px 0 8px 0' }}>
                                                        Select the database where this user will be created.
                                                    </p>
                                                    <div className="mb-4">
                                                        <Select
                                                            inputId="create-user-target-db"
                                                            name="targetDatabase"
                                                            options={databases}
                                                            value={selectedDb}
                                                            onChange={setSelectedDb}
                                                            placeholder="Select Database..."
                                                            classNamePrefix="react-select"
                                                            styles={{
                                                                control: (base) => ({ ...base, minHeight: '40px', borderColor: '#d1d5db', borderRadius: '8px' })
                                                            }}
                                                        />
                                                    </div>

                                                    <label className="d-block fw-bold mb-0" style={{ fontSize: '0.875rem', color: '#16191f' }}>
                                                        System Authorization Password (To Confirm) <span className="text-danger">*</span>
                                                    </label>
                                                    <p style={{ fontSize: '0.78rem', color: '#687078', margin: '2px 0 8px 0' }}>
                                                        System authorization password required to authorize this action.
                                                    </p>
                                                    <FormInputIcon 
                                                        showHeaderLabel={false}
                                                        name="SuperAdminPassword"
                                                        type={showSuperAdminPassword ? "text" : "password"}
                                                        errors={errors}
                                                        required={true}
                                                        maxLengthText=""
                                                        wrapperClassName="mb-0"
                                                        className=""
                                                        autoComplete="new-password"
                                                        placeholder="Enter system authorization password"
                                                        isShowHideIcon={true}
                                                        showPassword={showSuperAdminPassword}
                                                        onClickShowHide={setShowSuperAdminPassword}
                                                        validation={{...register("SuperAdminPassword", { required: "System Authorization Password is required." })}}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </form>
                    </Fragment>
                )}

                {step === 2 && (
                    <div className="text-center mb-5 mt-4">
                        <div className="mb-4">
                            <span className="bg-primary bg-opacity-10 text-primary rounded-circle d-inline-flex align-items-center justify-content-center" style={{ width: '64px', height: '64px' }}>
                                <FontAwesomeIcon icon={faEnvelopeOpenText} className="fs-3" />
                            </span>
                        </div>
                        <h4 className="text-body-highlight fw-bold mb-2">Verify user email</h4>
                        <p className="text-body-tertiary mb-0 fs-9 px-sm-4 px-md-5">
                            We've sent a 6-digit verification code to <br />
                            <strong className="text-body-emphasis">{watch("Email")}</strong>
                        </p>
                        
                        {error && (
                            <div className="auth-error-message mt-3 d-flex justify-content-center gap-2">
                                <FontAwesomeIcon icon={faTriangleExclamation} className="text-danger mt-1" />
                                <span className="text-danger">{error}</span>
                            </div>
                        )}
                        
                        <div className="verification-form mt-4 pt-2">
                            <form id="formCreateUserAccount" onSubmit={handleSubmit(onSubmit)}>
                                <div className="d-flex justify-content-center gap-2 gap-sm-3 mb-4">
                                    {[...Array(6)].map((_, i) => (
                                        <input
                                            key={i}
                                            type="text"
                                            maxLength={1}
                                            className="form-control text-center fw-bold fs-4 shadow-sm border-secondary-subtle p-0"
                                            style={{ width: '46px', height: '58px', borderRadius: '10px' }}
                                            {...register(`code${i}`)}
                                            ref={(el) => (inputsRef.current[i] = el)}
                                            onBeforeInput={handleBeforeInput}
                                            onChange={(e) => handleInput(e, i)}
                                            onKeyDown={(e) => handleKeyDown(e, i)}
                                        />
                                    ))}
                                </div>
                                <div className="text-center mt-3 mb-1">
                                    <span className="text-muted fs-9">Didn't receive the code? </span>
                                    <button 
                                        type="button" 
                                        className="btn btn-link p-0 text-decoration-none fw-bold fs-9" 
                                        onClick={() => sendCreationCode(getValues())}
                                        disabled={isSubmitting || resendCountdown > 0}
                                    >
                                        {resendCountdown > 0 ? `Resend Code (${resendCountdown}s)` : "Resend Code"}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}

                {step === 3 && successData && (
                    <div className="text-center py-4">
                        <div className="mb-4">
                            <span className="bg-success bg-opacity-10 text-success rounded-circle d-inline-flex align-items-center justify-content-center" style={{ width: '64px', height: '64px' }}>
                                <FontAwesomeIcon icon={faCheck} className="fs-3" />
                            </span>
                        </div>
                        <h4 className="text-body-highlight fw-bold mb-2">Account Created Successfully!</h4>
                        <p className="text-body-tertiary mb-4 fs-9">
                            The user account has been created. Please securely copy and share the credentials below with the user.
                        </p>

                        <div className="card bg-light border-0 shadow-none mx-auto" style={{ maxWidth: '400px' }}>
                            <div className="card-body text-start position-relative">
                                <button 
                                    className="btn btn-sm btn-outline-secondary position-absolute top-0 end-0 m-2"
                                    onClick={handleCopy}
                                    title="Copy to clipboard"
                                >
                                    <FontAwesomeIcon icon={copied ? faCheck : faCopy} className={copied ? "text-success" : ""} /> {copied ? "Copied!" : "Copy"}
                                </button>
                                
                                <div className="mb-3">
                                    <span className="d-block fs-10 text-uppercase fw-bold text-muted mb-1">Username</span>
                                    <div className="d-flex align-items-center gap-2">
                                        <span className="fs-8 fw-bolder text-body-emphasis">{successData.UserName}</span>
                                        <button 
                                            type="button" 
                                            className="btn btn-sm btn-link p-0 text-decoration-none" 
                                            onClick={() => handleCopySingle(successData.UserName, 'username')}
                                            title="Copy username"
                                        >
                                            <FontAwesomeIcon icon={copiedUsername ? faCheck : faCopy} className={copiedUsername ? "text-success" : "text-muted"} />
                                        </button>
                                    </div>
                                </div>
                                <div>
                                    <span className="d-block fs-10 text-uppercase fw-bold text-muted mb-1">Password</span>
                                    <div className="d-flex align-items-center gap-2">
                                        <span className="fs-8 fw-bolder text-body-emphasis font-monospace">{successData.Password}</span>
                                        <button 
                                            type="button" 
                                            className="btn btn-sm btn-link p-0 text-decoration-none" 
                                            onClick={() => handleCopySingle(successData.Password, 'password')}
                                            title="Copy password"
                                        >
                                            <FontAwesomeIcon icon={copiedPassword ? faCheck : faCopy} className={copiedPassword ? "text-success" : "text-muted"} />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>


                    </div>
                )}
            </div>
        </CenteredModal>
    );
}
